// Project-level offline configuration (Database Designer → Offline).
//
// Two things, both project-wide: the master switch (`application.offline`) and
// the device replication set — which tables travel to a handheld and under what
// per-device filter. Plus a readiness check that runs the SAME lint publish
// runs, so the designer never disagrees with what a bundle build will accept.

import api from './client'

export interface DeviceDataset {
  /** Physical table name. */
  table: string
  /** `down` = server → device (reference/allocated data); `up` = captured rows going back. */
  direction: 'down' | 'up'
  /**
   * SQL predicate scoping the pull to THIS device. Values must be `:name`
   * binds — `:device_user` and `:device_code` are built-ins, anything else must
   * be a declared project variable. Never string-interpolated.
   */
  filter?: string
  /** `full` refetches in scope; `watermark` fetches only what changed since the cursor. */
  mode?: 'full' | 'watermark'
  /** Required for `watermark`: a column that increases on every change. */
  watermark_column?: string
  /** Clear the device's copy before a full pull (a fresh allocation each day). */
  reset_on_pull?: boolean
  /** Required for `up`: the transaction object captured rows are REPLAYED through. */
  via_object?: string
  enabled?: boolean
  /** Server-computed, read-only. */
  _issues?: string[]
  /** Server-computed: the `:name` binds this filter needs resolved. */
  _requires?: string[]
}

export interface OfflineConfig {
  success: boolean
  enabled: boolean
  biz_logic: string
  db_mode: string
  version: number
  datasets: DeviceDataset[]
}

export interface OfflineReadiness {
  success: boolean
  ready: boolean
  problems: string[]
}

export async function getOfflineConfig(project: string): Promise<OfflineConfig> {
  const { data } = await api.get('/offline/config', { params: { project } })
  return data
}

/**
 * Flip the master switch. Enabling also forces `biz_logic` to Python server-side
 * — one business-logic source has to run on both the server engine and SQLite on
 * the device, and only Python does. The response says whether that happened so
 * the UI can tell the user rather than change it behind their back.
 */
export async function setOfflineEnabled(
  project: string, enabled: boolean,
): Promise<{ success: boolean; enabled: boolean; biz_logic_switched_to_python: boolean }> {
  const { data } = await api.post('/offline/enable', { project, enabled })
  return data
}

export async function saveDeviceDatasets(
  project: string, datasets: DeviceDataset[],
): Promise<{ success: boolean; issues: string[] }> {
  const { data } = await api.post('/offline/datasets/save', { project, datasets })
  return data
}

export async function getOfflineReadiness(project: string): Promise<OfflineReadiness> {
  const { data } = await api.get('/offline/readiness', { params: { project } })
  return data
}
